import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { aboutImages } from '../data/mock';
import { Users, Award, Heart, TrendingUp } from 'lucide-react';

const About = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-400 via-yellow-500 to-yellow-600">
      {/* Hero Section */}
      <section className="relative h-[400px] overflow-hidden">
        <img
          src={aboutImages[0]}
          alt="देशी Stuff Restaurant"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/90 to-black/50 flex items-center">
          <div className="container mx-auto px-4">
            <h1 className="text-5xl md:text-6xl font-black text-white mb-4">
              <span className="text-yellow-400">OUR</span> STORY
            </h1>
            <p className="text-xl md:text-2xl text-yellow-300 font-bold">From roadside stall to a growing chain</p>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-16 bg-gradient-to-b from-yellow-500 to-yellow-600">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-black mb-6 text-center text-white">
              AUTHENTIC PUNJABI TASTE, <span className="text-black">MODERN APPROACH</span>
            </h2>
            
            <div className="space-y-6 text-lg text-white font-medium bg-black/20 backdrop-blur-sm p-8 rounded-2xl border-4 border-black">
              <p className="text-xl font-bold text-yellow-300">
                Me and my wife started this brand in 2016 with a roadside stall.
              </p>
              
              <p>
                देशी Stuff started with a simple vision: bring authentic Punjabi stuffed parathas to everyone—fresh, hygienic, and loaded with taste. We're not just another local dhaba. We're building a scalable food brand that combines traditional recipes with modern food service standards.
              </p>
              
              <p>
                Every paratha we serve is made fresh, with quality ingredients and generous stuffing. We don't compromise on butter, we don't skimp on filling, and we never sacrifice taste for profit. That's the देशी Stuff promise.
              </p>
              
              <p>
                Today, we operate 6 outlets across Ahmedabad, serving thousands of happy customers every day. Our focus on consistency, hygiene, and authentic taste has made us a trusted name for students, professionals, and families alike.
              </p>
              
              <p className="font-bold text-xl text-yellow-300">
                We're not just selling parathas. We're building a movement to bring back real, satisfying, desi food in a fast-paced world.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 bg-yellow-400">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-black mb-12 text-center text-white">
            WHY <span className="text-black">देशी STUFF?</span>
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Card className="border-4 border-black hover:shadow-xl transition-shadow text-center bg-white">
              <CardContent className="p-8">
                <div className="bg-black w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Heart className="w-8 h-8 text-yellow-400" />
                </div>
                <h3 className="text-xl font-black mb-3 text-black">Authentic Taste</h3>
                <p className="text-gray-800 font-medium">Traditional Punjabi recipes passed down generations. Real taste, no shortcuts.</p>
              </CardContent>
            </Card>

            <Card className="border-4 border-black hover:shadow-xl transition-shadow text-center bg-white">
              <CardContent className="p-8">
                <div className="bg-black w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Award className="w-8 h-8 text-yellow-400" />
                </div>
                <h3 className="text-xl font-black mb-3 text-black">Quality Standards</h3>
                <p className="text-gray-800 font-medium">Maintained hygiene, quality ingredients, consistent preparation across all outlets.</p>
              </CardContent>
            </Card>

            <Card className="border-4 border-black hover:shadow-xl transition-shadow text-center bg-white">
              <CardContent className="p-8">
                <div className="bg-black w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-yellow-400" />
                </div>
                <h3 className="text-xl font-black mb-3 text-black">Customer First</h3>
                <p className="text-gray-800 font-medium">Every paratha made fresh. Quick service. Value for money. Customer satisfaction guaranteed.</p>
              </CardContent>
            </Card>

            <Card className="border-4 border-black hover:shadow-xl transition-shadow text-center bg-white">
              <CardContent className="p-8">
                <div className="bg-black w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <TrendingUp className="w-8 h-8 text-yellow-400" />
                </div>
                <h3 className="text-xl font-black mb-3 text-black">Growing Brand</h3>
                <p className="text-gray-800 font-medium">Expanding rapidly across Ahmedabad. Proven business model. Strong customer base.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Image Gallery */}
      <section className="py-16 bg-gradient-to-b from-yellow-600 to-yellow-500">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-black mb-12 text-center text-white">
            OUR <span className="text-black">OUTLETS</span>
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {aboutImages.map((image, index) => (
              <div key={index} className="relative h-80 overflow-hidden rounded-xl shadow-lg group border-4 border-black">
                <img
                  src={image}
                  alt={`देशी Stuff outlet ${index + 1}`}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-black text-yellow-400">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <h3 className="text-5xl font-black mb-2">6+</h3>
              <p className="text-xl font-bold text-white">Outlets</p>
            </div>
            <div>
              <h3 className="text-5xl font-black mb-2">360+</h3>
              <p className="text-xl font-bold text-white">Daily Orders</p>
            </div>
            <div>
              <h3 className="text-5xl font-black mb-2">100%</h3>
              <p className="text-xl font-bold text-white">Fresh & Hot</p>
            </div>
            <div>
              <h3 className="text-5xl font-black mb-2">5★</h3>
              <p className="text-xl font-bold text-white">Customer Rating</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
